<template>
    <div
        v-loading="loadingInfo"
        class="register-form"
    >
        <div>
            <h1 class="app-h1">
                {{ registerInfo.attributes.title }}
            </h1>
            <br>
        </div>
        <div
            v-if="availableForRegister"
            class="register-form__container"
        >
            <div class="participant-form">
                <div class="participant-form__container">
                    <el-form
                        ref="form"
                        label-position="top"
                        :rules="rules"
                        :model="form"
                        @submit.native.prevent="_loginAttempt"
                    >
                        <el-form-item
                            class="register-form__phone-field"
                            :show-message="isPhoneErrorVisible"
                            prop="phone"
                            label="Введите номер телефона:"
                        >
                            <app-phone-input
                                v-model="form.phone"
                                placeholder="Введите номер телефона"
                                @change="_onPhoneChanged"
                            />
                        </el-form-item>
                        <el-form-item
                            align="center"
                        >
                            <el-button
                                type="primary"
                                :loading="submitLoading"
                                :disabled="submitLoading"
                                @click="_loginAttempt"
                            >
                                Далее
                            </el-button>
                        </el-form-item>
                    </el-form>
                </div>
            </div>
        </div>
        <confirm-call-modal
            ref="smsModal"
            class="abow_dialog"
            :call-code-text="callCodeText"
            :show-fallback-action="showCallCodeFallbackAction"
            :time-out="timeOut"
            :loading="modalLoading"
            :timer-start="timerStart"
            @confirm-enter="_goToLk"
            @close-modal="_onCloseModal"
            @confirm-code="_confirmCallCode"
            @fallback-click="_onFallbackClick"
            @repeat-call="_onRepeatCall"
        />
        <confirm-user-call-modal
            ref="userCallModal"
            :loading="userCallModalLoading"
            :description-text="userCallText"
            :call-phone="userCallPhone"
            :cooldown-seconds-left="userCallCooldownSecondsLeft"
            :expires-at="userCallExpiresAt"
            :time-out="timeOut"
            :show-actions="showUserCallActions"
            :show-fallback-action="showUserCallFallbackAction"
            @close-modal="_onUserCallModalClose"
            @repeat-call="_onUserCallRepeat"
            @fallback-click="_onFallbackClick"
        />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { ClientAuth } from '@/api';
import AppPhoneInput from '@/components/form/AppPhoneInput';
import ConfirmCallModal from '@/containers/lk/components/confirmSmsModal/ConfirmCallModal';
import ConfirmUserCallModal from '@/containers/lk/components/confirmSmsModal/ConfirmUserCallModal';

export default {
    name: 'Auth',
    components: {
        ConfirmCallModal,
        ConfirmUserCallModal,
        AppPhoneInput
    },
    data() {
        return {
            form: {},
            previousPhone: '',
            verificationOptions: [],
            cooldownSeconds: 20,
            startingPrimaryVerification: false,
            startingFallbackVerification: false,
            userCallCooldownSecondsLeft: 0,
            userCallCooldownTimer: null,
            userCallPolling: false,
            userCallPollingTimer: null,
            userCallPollingRequestActive: false,
            userCallLoginCompleted: false,
            userCallPhone: '',
            userCallExpiresAt: '',
            loadingInfo: true,
            availableForRegister: false,
            registerInfo: {
                attributes: {
                    title: 'Вход в личный кабинет'
                }
            },
            isPhoneValid: false,
            isPhoneTouched: false,
            wasValidated: false,
            timeOut: 0,
            timerStart: false,
            timer: null,
            rules: {
                phone: [
                    {
                        required: true,
                        trigger: 'blur',
                        validator: (rule, value, callback) => {
                            if (!value) {
                                return callback(new Error('Укажите  телефон'));
                            }
                            if (this.isPhoneValid) {
                                return callback();
                            }
                            return callback(new Error('Проверьте телефон'));
                        }
                    }
                ]
            }
        };
    },
    computed: {
        ...mapGetters('lk/participant', [
            'authorizing'
        ]),
        isPhoneLengthEnough() {
            const phone = this.form.phone;
            return phone ? phone.length > 11 : false;
        },

        isPhoneErrorVisible() {
            return this.wasValidated || this.isPhoneLengthEnough;
        },

        modalLoading() {
            return this.authorizing || this.startingPrimaryVerification || this.startingFallbackVerification;
        },

        submitLoading() {
            return this.authorizing || this.startingPrimaryVerification;
        },

        userCallModalLoading() {
            return this.startingFallbackVerification || this.userCallCooldownSecondsLeft > 0;
        },

        sortedVerificationOptions() {
            return [...this.verificationOptions].sort((a, b) => {
                return (a.priority || 0) - (b.priority || 0);
            });
        },

        primaryOption() {
            return this.sortedVerificationOptions.find(option => option.kind === 'primary') || null;
        },

        fallbackOption() {
            return this.sortedVerificationOptions.find(option => option.kind === 'fallback') || null;
        },

        callCodeText() {
            const callCodeOption = this.verificationOptions.find(option => option.type === 'call_code');

            return callCodeOption
                ? callCodeOption.label
                : 'Последние 4 цифры номера телефона, который сейчас вам позвонит';
        },

        userCallText() {
            return 'Сделайте бесплатный исходящий звонок с вашего телефона на этот номер, чтобы пройти идентификацию.';
        },

        showCallCodeFallbackAction() {
            return Boolean(
                this.primaryOption
                && this.primaryOption.type === 'call_code'
                && this.fallbackOption
                && this.timeOut === 0
            );
        },

        showUserCallActions() {
            return Boolean(this.primaryOption && this.primaryOption.type === 'user_call');
        },

        showUserCallFallbackAction() {
            return Boolean(
                this.primaryOption
                && this.primaryOption.type === 'user_call'
                && this.fallbackOption
                && this.timeOut === 0
            );
        }
    },
    mounted() {
        this._fetchVerificationOptions();
        this.getEventInfo()
            .then(response => {
                this.availableForRegister = true;
                this.registerInfo = response.data.data;
            })
            .catch(error => {
                this.availableForRegister = false;
                this.registerInfo = error.response.data.data;
            })
            .finally(() => {
                this.loadingInfo = false;
            });
    },
    methods: {
        ...mapActions('lk/participant', [
            'authAttempt',
            'checkSmsCode',
            'getEventInfo'
        ]),

        _fetchVerificationOptions() {
            return ClientAuth.getVerificationOptions()
                .then(response => {
                    this.verificationOptions = response.data.verification_options || [];
                    this.cooldownSeconds = response.data.cooldown_seconds || 20;
                })
                .catch(() => {
                    this.verificationOptions = [];
                    this.cooldownSeconds = 20;
                });
        },

        _startTimer(duration = this.cooldownSeconds) {
            this._clearTimer();
            this.timeOut = duration;
            this.timerStart = true;
            this.timer = setInterval(() => {
                if (this.timeOut === 0) {
                    clearInterval(this.timer);
                    return;
                }
                this.timeOut--;
            }, 1000);
        },

        _handleCooldownResponse(response, option = this.primaryOption) {
            const cooldownSeconds = response && response.data && response.data.cooldown_seconds
                ? Number(response.data.cooldown_seconds)
                : this.cooldownSeconds;

            this.cooldownSeconds = cooldownSeconds || this.cooldownSeconds;

            if (option && option.type === 'user_call') {
                this._stopUserCallPolling();
                this.userCallPhone = '';
                this.userCallExpiresAt = '';
                this.$refs.smsModal.closeModal();
                this.$refs.userCallModal.openModal();
                this._startUserCallCooldown(this.cooldownSeconds, option);
            } else {
                this.$refs.userCallModal.closeModal();
                this.$refs.smsModal.openModal();
                this._startTimer(this.cooldownSeconds);
            }

            this.$message.warning(`Попробуйте через ${this.cooldownSeconds} сек.`);
        },

        _startUserCallCooldown(duration = this.cooldownSeconds, option = null) {
            this._stopUserCallCooldown();
            this.userCallCooldownSecondsLeft = duration;
            this.userCallCooldownTimer = setInterval(() => {
                if (this.userCallCooldownSecondsLeft === 0) {
                    this._stopUserCallCooldown();
                    this._startVerificationForOption(option || this.primaryOption, { isPrimary: true });
                    return;
                }

                this.userCallCooldownSecondsLeft--;
            }, 1000);
        },

        _stopUserCallCooldown() {
            clearInterval(this.userCallCooldownTimer);
            this.userCallCooldownTimer = null;
            this.userCallCooldownSecondsLeft = 0;
        },

        _clearTimer() {
            clearInterval(this.timer);
        },

        _onCloseModal() {
            this.$refs.smsModal.closeModal();
        },

        _onUserCallModalClose() {
            this._stopUserCallCooldown();
            this._stopUserCallPolling();
            this.$refs.userCallModal.closeModal();
        },

        _goToLk() {
            this.$router.push({ name: 'LkDashboard' });
        },

        _confirmCallCode({ code }) {
            this.$accessManager.loginUser({
                phone: this.form.phone,
                code
            })
                .catch(err => {
                    this.$message.error('Произошла ошибка на сервере');
                });
        },

        _openPrimaryVerificationModal() {
            this.$refs.smsModal.openModal();
            this._startTimer();
            this.previousPhone = this.form.phone;

            setTimeout(() => {
                this.$message.success('Ожидайте звонок для подтверждения телефона');
                this.$refs.smsModal.$refs.codeInput.focus();
            }, 500);
        },

        _startUserCallVerification(option, { isPrimary = false } = {}) {
            this.userCallLoginCompleted = false;

            if (isPrimary) {
                this.startingPrimaryVerification = true;
            } else {
                this.startingFallbackVerification = true;
            }

            ClientAuth.startVerification({
                phone: this.form.phone,
                verificationType: option.type
            })
                .then(response => {
                    this._stopUserCallCooldown();
                    this.userCallPhone = response.data.call_phone || '';
                    this.userCallExpiresAt = response.data.expires_at || '';
                    this.$refs.smsModal.closeModal();
                    this.$refs.userCallModal.openModal();
                    if (isPrimary) {
                        this._startTimer();
                    } else {
                        this._clearTimer();
                        this.timeOut = 0;
                    }
                    this._startUserCallPolling();
                })
                .catch(err => {
                    const errorResponse = err && err.response ? err.response : err;

                    if (errorResponse && errorResponse.status === 401 && errorResponse.data && errorResponse.data.canVerify === false) {
                        this._handleCooldownResponse(errorResponse, option);
                        return;
                    }
                    this.$message.error('Не удалось получить номер для входа');
                })
                .finally(() => {
                    if (isPrimary) {
                        this.startingPrimaryVerification = false;
                    } else {
                        this.startingFallbackVerification = false;
                    }
                });
        },

        _startCallCodeVerification(option, { isPrimary = false } = {}) {
            if (isPrimary) {
                this.startingPrimaryVerification = true;
            } else {
                this.startingFallbackVerification = true;
            }

            ClientAuth.startVerification({
                phone: this.form.phone,
                verificationType: option.type
            })
                .then(() => {
                    this._stopUserCallCooldown();
                    this._stopUserCallPolling();
                    this.$refs.userCallModal.closeModal();
                    this._openPrimaryVerificationModal();

                    if (!isPrimary) {
                        this._startTimer();
                    }
                })
                .catch(err => {
                    const errorResponse = err && err.response ? err.response : err;

                    if (errorResponse && errorResponse.status === 401 && errorResponse.data && errorResponse.data.canVerify === false) {
                        this._handleCooldownResponse(errorResponse, option);
                        return;
                    }

                    this.$message.error('Не удалось начать подтверждение звонком');
                })
                .finally(() => {
                    if (isPrimary) {
                        this.startingPrimaryVerification = false;
                    } else {
                        this.startingFallbackVerification = false;
                    }
                });
        },

        _startVerificationForOption(option, { isPrimary = false } = {}) {
            if (!option) {
                return;
            }

            if (option.type === 'user_call') {
                this._startUserCallVerification(option, { isPrimary });
                return;
            }

            this._startCallCodeVerification(option, { isPrimary });
        },

        _onFallbackClick() {
            if (!this.fallbackOption) {
                return;
            }

            this._startVerificationForOption(this.fallbackOption);
        },

        _loginAttempt() {
            this.validate()
                .then(() => {
                    this.$accessManager.authAttempt({ phone: this.form.phone })
                        .catch(err => {
                            if (err.status === 422) {
                                this.isPhoneValid = false;
                                this.$refs.form.validateField('phone');
                                return;
                            }
                            if (err.status === 401 && err.data && err.data.canVerify === false) {
                                if (this.primaryOption && this.primaryOption.type === 'user_call') {
                                    this._startVerificationForOption(this.primaryOption, { isPrimary: true });
                                    return;
                                }

                                this._handleCooldownResponse(err, this.primaryOption);
                                return;
                            }

                            if (err.status === 401) {
                                this._startVerificationForOption(this.primaryOption, { isPrimary: true });
                                return;
                            }

                            this.$message.error('Произошла ошибка на сервере');
                        });
                })
                .catch(err => {
                    this.$refs.form.validateField('phone');
                });
        },

        _onRepeatCall() {
            const callCodeOption = this.verificationOptions.find(option => option.type === 'call_code');

            this._startVerificationForOption(callCodeOption, {
                isPrimary: this.primaryOption && this.primaryOption.type === 'call_code'
            });
        },

        _onUserCallRepeat() {
            const userCallOption = this.verificationOptions.find(option => option.type === 'user_call');

            this._startVerificationForOption(userCallOption, {
                isPrimary: this.primaryOption && this.primaryOption.type === 'user_call'
            });
        },

        _startUserCallPolling() {
            this._stopUserCallPolling();
            this.userCallPolling = true;
            this._pollUserCallStatus();
            this.userCallPollingTimer = setInterval(() => {
                this._pollUserCallStatus();
            }, 1500);
        },

        _stopUserCallPolling() {
            this.userCallPolling = false;
            this.userCallPollingRequestActive = false;
            clearInterval(this.userCallPollingTimer);
            this.userCallPollingTimer = null;
        },

        _pollUserCallStatus() {
            const userCallOption = this.verificationOptions.find(option => option.type === 'user_call');

            if (!userCallOption || this.startingFallbackVerification || this.userCallPollingRequestActive || this.userCallLoginCompleted) {
                return;
            }

            this.userCallPollingRequestActive = true;

            this.$accessManager.checkVerificationStatus({
                phone: this.form.phone,
                verificationType: userCallOption.type
            })
                .then(response => {
                    if (response.status === 'verified') {
                        this.userCallLoginCompleted = true;
                        this._stopUserCallPolling();
                        return;
                    }

                    if (response.status === 'expired') {
                        this._stopUserCallPolling();
                        this.$message.error('Время подтверждения истекло');
                        return;
                    }

                    if (response.call_phone) {
                        this.userCallPhone = response.call_phone;
                    }

                    if (response.expires_at) {
                        this.userCallExpiresAt = response.expires_at;
                    }
                })
                .catch(() => {
                })
                .finally(() => {
                    if (!this.userCallLoginCompleted) {
                        this.userCallPollingRequestActive = false;
                    }
                });
        },

        _onPhoneChanged({ phone, isValid, country }) {
            this.wasValidated = false;
            this.isPhoneValid = isValid;
            this.isPhoneTouched = true;
            if (isValid) {
                this.wasValidated = true;
                this.$refs.form.validateField('phone');
            }
        },

        validate() {
            this.wasValidated = true;
            return new Promise((resolve, reject) => {
                if (!this.form.phone) {
                    this.$message('Укажите телефон');
                    return reject(new Error('empty_phone'));
                }

                this.$refs.form.validate()
                    .then(isValid => {
                        this.isValid = true;
                        resolve(isValid);
                    })
                    .catch(err => {
                        this.isValid = false;
                        reject(err);
                    });
            });
        },

        _formatDateTime(value) {
            if (!value) {
                return '';
            }

            const date = new Date(value);

            if (Number.isNaN(date.getTime())) {
                return value;
            }

            return date.toLocaleString('ru-RU');
        }
    },
    beforeDestroy() {
        this._stopUserCallCooldown();
        this._stopUserCallPolling();
    }
};
</script>

<style lang="scss">
@media (max-width: 450px) {
    .register-form__phone-field > .el-form-item__label {
        font-size: 18px;
    }
}
</style>
